import '@livekit/components-styles';
import clsx from 'clsx';
import { ConnectionState, Room, Track } from 'livekit-client';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RoomAudioRenderer,
  RoomContext,
  TrackToggle,
  useConnectionState,
  MediaDeviceMenu,
} from '@livekit/components-react';
import {
  Button,
  Modal,
  analyticsClient,
} from '@spotted-zebra-uk/ui-components';
import Icon from 'components/atoms/Icon';
import { AgentFeed } from '../AgentFeed/AgentFeed';
import { ParticipantFeed } from '../ParticipantFeed/ParticipantFeed';
import { AgentStatusBadge } from '../AgentStatusBadge';
import styles from './ConferenceRoom.module.scss';
import { HEAP_ANALYTICS_EVENTS } from '@/constants/analytics';
import { EndCallConfig } from '../ConferenceLobby/ConferenceLobby';

interface ConferenceRoomProps {
  room: Room;
  onEndCall: () => void;
  endCallConfig?: EndCallConfig;
}

export function ConferenceRoom({
  room,
  onEndCall,
  endCallConfig,
}: ConferenceRoomProps) {
  const { t } = useTranslation();
  const resolvedEndCallConfig: EndCallConfig = endCallConfig ?? {
    title: t('aiInterview.endInterview.title'),
    message: t('aiInterview.endInterview.message'),
    confirmLabel: t('aiInterview.endInterview.confirm'),
  };
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showEndCallModal, setShowEndCallModal] = useState(false);

  const handleEndCallClick = useCallback(() => {
    setShowEndCallModal(true);

    analyticsClient.trackEvent({
      name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_CONFERENCE_ROOM_END_CALL_BUTTON_CLICK,
    });
  }, []);

  const handleCancelEndCall = useCallback(() => {
    setShowEndCallModal(false);

    analyticsClient.trackEvent({
      name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_CONFERENCE_ROOM_END_CALL_CANCEL_MODAL_CLICK,
    });
  }, []);

  const handleConfirmEndCall = useCallback(async () => {
    setShowEndCallModal(false);
    setIsDisconnecting(true);

    // Stop all local tracks to release camera/microphone
    room.localParticipant.trackPublications.forEach(publication => {
      if (publication.track) {
        publication.track.stop();
        // Also stop the underlying MediaStreamTrack if available
        if (publication.track.mediaStreamTrack) {
          publication.track.mediaStreamTrack.stop();
        }
      }
    });

    analyticsClient.trackEvent({
      name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_CONFERENCE_ROOM_END_CALL_CONFIRM_MODAL_CLICK,
    });

    // Call onEndCall which triggers leaveConference
    onEndCall();
  }, [room, onEndCall]);

  useEffect(() => {
    analyticsClient.trackEvent({
      name: HEAP_ANALYTICS_EVENTS.CANDIDATE_AI_INTERVIEW_CONFERENCE_ROOM_PAGE_VISIT,
    });
  }, []);

  // Critical: Cleanup media streams when component unmounts
  // This ensures camera/microphone are released even if user navigates away
  useEffect(() => {
    return () => {
      room.localParticipant.trackPublications.forEach(publication => {
        if (publication.track) {
          // Stop the LiveKit track
          publication.track.stop();
          // Also stop the underlying MediaStreamTrack to ensure hardware is released
          if (publication.track.mediaStreamTrack) {
            publication.track.mediaStreamTrack.stop();
          }
        }
      });
    };
  }, [room]);

  return (
    <RoomContext.Provider value={room}>
      <RoomAudioRenderer />
      <div className={styles.conferenceRoom}>
        <div data-lk-theme="default" className={styles.conferenceContainer}>
          <ConferenceContent />

          <div className={styles.controlsContainer}>
            <div className={styles.controls}>
              <TrackToggle
                source={Track.Source.Microphone}
                showIcon={true}
                className={styles.trackToggle}
              />

              <MediaDeviceMenu
                className={clsx(
                  styles.trackToggle,
                  styles.mediaDeviceMenuButton
                )}
              >
                <Icon icon="settings" />
              </MediaDeviceMenu>

              <Button
                onClick={handleEndCallClick}
                disabled={isDisconnecting}
                className={styles.endCallButton}
              >
                {isDisconnecting ? t('aiInterview.leaving') : t('aiInterview.endCall')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* @ts-ignore */}
      <Modal
        isOpen={showEndCallModal}
        onClose={handleCancelEndCall}
        className={styles.endCallModal}
      >
        <h3 className={styles.endCallModalTitle}>{resolvedEndCallConfig.title}</h3>
        <p className={styles.endCallModalText}>{resolvedEndCallConfig.message}</p>
        <div className={styles.endCallModalButtons}>
          <Button variant="secondary" onClick={handleCancelEndCall}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleConfirmEndCall}>
            {resolvedEndCallConfig.confirmLabel}
          </Button>
        </div>
      </Modal>
    </RoomContext.Provider>
  );
}

function ConferenceContent() {
  const { t } = useTranslation();
  const connectionState = useConnectionState();

  const isConnecting =
    connectionState === ConnectionState.Connecting ||
    connectionState === ConnectionState.Reconnecting;

  return (
    <div className={styles.videoContainer}>
      {isConnecting && (
        <div className={styles.connectingOverlay}>
          <div className={styles.connectingSpinner} />
          <p>
            {connectionState === ConnectionState.Reconnecting
              ? t('aiInterview.reconnecting')
              : t('aiInterview.connectingToInterviewer')}
          </p>
        </div>
      )}

      <section>
        <div
          className={styles.interviewSection}
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <div className={styles.agentSection}>
            <AgentFeed />
          </div>
          <ParticipantFeed />
        </div>
      </section>

      <div className={styles.statusBadgeContainer}>
        <AgentStatusBadge />
      </div>
    </div>
  );
}
